import { z } from 'zod';

const schemaKeyValuePair = z.object({
    key: z.string(),
    value: z.string(),
});

const schemaPrivatePhone = z.object({
    number: z.string(),
    prefix: z.string(),
});

const schemaContact = z.object({
    businessPhone: z
        .object({
            number: z.string(),
            prefix: z.string(),
        })
        .optional(),
    email: z.string().optional(),
    mobilePhone: z
        .object({
            number: z.string(),
            prefix: z.string(),
        })
        .optional(),
    privatePhone: schemaPrivatePhone.optional(),
});

const schemaBankAccount = z.object({
    bankName: z.string().optional(),
    bic: z.string().optional(),
    iban: z.string().optional(),
});

// PC24 expects ISO 3166-1 alpha-3 here (e.g. "DEU") — an alpha-2 code would be silently dropped.
const schemaAlpha3 = z.string().regex(/^[A-Z]{3}$/);

const schemaAddress = z.object({
    city: z.string().optional(),
    country: schemaAlpha3.optional(),
    houseNumber: z.string().optional(),
    street: z.string().optional(),
    zipCode: z.string().optional(),
});

export const schemaProductUserPerson = z.object({
    address: schemaAddress.optional(),
    bankAccount: schemaBankAccount.optional(),
    contact: schemaContact.optional(),
    dateOfBirth: z.string().optional(),
    firstName: z.string(),
    gender: z.enum(['MALE', 'FEMALE']),
    lastName: z.string(),
    nationality: schemaAlpha3.optional(),
    title: z.string().optional(),
});

export type SchemaPc24ProductUserPerson = z.infer<typeof schemaProductUserPerson>;

export const schemaPc24ImportRequest = z.object({
    // PC24 spells this field "authentification" — confirmed against the live createTestRequestJson
    // contract. NOTE: the spec PDF's JSON example (p.41) shows the "-cation" spelling; the live
    // contract wins. Do not "fix" the spelling back.
    authentification: z.null().optional(),
    customerActionId: z.string().optional(),
    externalCustomerId: z.string().optional(),
    // Rev. 2: the sending adviser's Procheck ID, snapshotted from the Requested event; omitted
    // entirely (never an empty string) when the adviser has none.
    ownerPartnerId: z.string().min(1).optional(),
    product: z.literal('CREDIT'),
    productFields: z
        .object({
            keyValuePair: z.array(schemaKeyValuePair),
        })
        .optional(),
    productUserPerson: schemaProductUserPerson,
    productUserPerson2: schemaProductUserPerson.optional(),
});

export type SchemaPc24ImportRequest = z.infer<typeof schemaPc24ImportRequest>;
